/* eslint-disable id-length */
import { useEffect, useMemo } from "react";
import { ChartData, TooltipModel } from "chart.js";

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "app/components/shared/HoverCard";
import { useChart } from "app/components/shared/Chart/chart";

import LineChart, { LineChartUnit } from "../LineChart";
import SparkLineChart, { withGradientFillLine } from "../SparkLineChart";
import { Card, CardSection, CardChart, CardLabel, CardStat } from "../Card";
import { colorsForIndex, ThemeDefaultTrend } from "../Theme";
import { TimePeriod } from "../Renderer";
import {
  formatPercentage,
  formatTooltipDateString,
  getLatestValue,
  getTimePeriodString,
} from "./utils";
import { ChartLegend } from "../ChartLegend";

interface Props {
  dataset: {
    x: number;
    active: number;
    percentage: number;
    running_jobs: number;
  }[];
}

const percentFormat = new Intl.NumberFormat("default", {
  style: "percent",
});

export function QueueAgentUtilizationSpark({ dataset }: Props) {
  const latestPercentage = getLatestValue(dataset, "percentage");
  const formattedPercentage = new Intl.NumberFormat("default", {
    style: "percent",
  }).format(latestPercentage / 100.0); // "percent" format expects a value between 0 and 1

  const activeAgents = getLatestValue(dataset, "active");

  return (
    <Card>
      <CardSection>
        <CardLabel>Agent utilization</CardLabel>
      </CardSection>

      <CardSection>
        <CardStat
          label={`${activeAgents} active agents`}
          value={formattedPercentage}
          size="lg"
          layout="stacked"
        />
      </CardSection>

      <CardChart>
        <SparkLineChart
          data={{
            labels: dataset.map(({ x }) => x),
            datasets: [
              withGradientFillLine(
                dataset.map(({ percentage }) => percentage),
                ThemeDefaultTrend,
              ),
            ],
          }}
        />
      </CardChart>
    </Card>
  );
}

interface DetailProps {
  scope: "queue" | "cluster" | "organization";
  dataset: {
    label: string;
    data: {
      x: number;
      active: number;
      percentage: number;
      running_jobs: number;
    }[];
  }[];
  timePeriod: TimePeriod;
}

export function QueueAgentUtilizationDetail({
  scope,
  dataset,
  timePeriod,
}: DetailProps) {
  const data: ChartData = useMemo(
    () => ({
      labels: dataset[0].data.map(({ x }) => x),
      datasets: dataset.map(({ label, data }, index) => ({
        label,
        data: data.map((datapoint) => datapoint.percentage),
        ...colorsForIndex(scope === "queue" ? index + 1 : index),
      })),
    }),
    [dataset],
  );

  const activeAgents = dataset.reduce(
    (acc, dataset) => acc + getLatestValue(dataset.data, "active"),
    0,
  );
  const runningJobs = dataset.reduce(
    (acc, dataset) => acc + getLatestValue(dataset.data, "running_jobs"),
    0,
  );

  const { chartRef, updateData } = useChart();
  useEffect(() => {
    updateData(data);
  }, [data, updateData]);

  return (
    <Card className="lg:flex-row">
      <div className="flex flex-col grow gap-3 min-w-0">
        <CardSection>
          <CardLabel description={getTimePeriodString(timePeriod)}>
            Agent utilization
          </CardLabel>

          <CardSection className="flex-col">
            <CardStat value={activeAgents} label="Active Agents" />
            <CardStat value={runningJobs} label="Running Jobs" />
          </CardSection>
        </CardSection>

        <CardChart>
          <LineChart
            ref={chartRef}
            data={data}
            tooltip={Tooltip}
            units={{ y: LineChartUnit.percentage }}
          />
        </CardChart>
      </div>

      <ChartLegend
        datasets={data.datasets}
        chartRef={chartRef}
        columns={{
          Utilization: (_, index) => (
            <span>
              {percentFormat.format(
                getLatestValue(dataset[index].data, "percentage") / 100.0,
              )}
            </span>
          ),
          "Active agents": (_, index) => (
            <span>{getLatestValue(dataset[index].data, "active")}</span>
          ),
        }}
      />
    </Card>
  );
}

const Tooltip = ({ tooltip }: { tooltip: TooltipModel<"line"> }) => {
  const dateString = formatTooltipDateString(tooltip.dataPoints[0].parsed.x);

  return (
    <HoverCard open={true}>
      <HoverCardTrigger />
      <HoverCardContent side="right" className="w-auto px-2 py-2.5">
        <div className="flex flex-col gap-1.5">
          <div className="font-semibold text-xs">{dateString}</div>

          {tooltip.dataPoints.map(({ dataset, parsed }, index) => {
            return (
              <div
                key={index}
                className="font-semibold text-xs"
                style={{ color: dataset.borderColor as string }}
              >
                {dataset.label}:{" "}
                <span className="font-medium text-charcoal-300">
                  {formatPercentage(parsed.y)}
                </span>
              </div>
            );
          })}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};
